import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MapPin, CreditCard, Truck, ArrowLeft, Mail, User, ShoppingBag, Zap, Tag, X, Check } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import FreeShippingBar from '../components/FreeShippingBar';
import { getActiveAffiliateRef, clearAffiliateRef } from '../utils/affiliateTracking';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FREE_SHIPPING_THRESHOLD = 250;

// Shipping rate calculator
const calculateShipping = (subtotal, method = 'standard') => {
  if (method === 'express') {
    if (subtotal >= 250) return 14.99;
    if (subtotal >= 100) return 24.99;
    if (subtotal >= 50) return 19.99;
    return 14.99;
  }
  // Standard shipping
  if (subtotal >= 250) return 0;
  if (subtotal >= 100) return 12.99;
  if (subtotal >= 50) return 9.99;
  return 7.99;
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, loading: cartLoading } = useCart();
  const { user } = useAuth();
  
  // Guest cart from localStorage
  const [guestCart, setGuestCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem('guest_cart');
      return savedCart ? JSON.parse(savedCart) : { items: [], total: 0 };
    } catch {
      return { items: [], total: 0 };
    }
  });
  
  const [guestInfo, setGuestInfo] = useState({
    email: '',
  });
  
  const [shippingMethod, setShippingMethod] = useState('standard');
  
  const [shippingAddress, setShippingAddress] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States'
  });
  const [loading, setLoading] = useState(false);

  // Promo code state — discounts NEVER stack with volume tier; the highest of the two wins per item.
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // {code, discount_percent, description}
  const [promoLoading, setPromoLoading] = useState(false);

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoLoading(true);
    try {
      const { data } = await axios.post(`${API}/promo/validate`, { code });
      setAppliedPromo(data);
      toast.success(`${data.code} applied — ${data.description}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid or expired promo code');
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
  };

  // Guest cart is loaded from localStorage via the lazy useState initializer above.

  // Determine which cart to use
  const activeCart = user ? cart : guestCart;

  // Client-side mirror of backend discount logic: per-item, take max(volume_pct, promo_pct).
  // Volume tier already baked into activeCart line subtotals (cart.py applies it). So when a
  // promo brings the per-item discount HIGHER than the volume tier, we need to recompute.
  const promoPct = appliedPromo ? Number(appliedPromo.discount_percent) || 0 : 0;
  const volumePct = (qty) => (qty >= 10 ? 10 : qty >= 5 ? 5 : 0);

  let subtotal = 0;
  let originalSubtotal = 0; // pre-discount, for showing savings
  (activeCart.items || []).forEach((item) => {
    const qty = item.quantity;
    const original = item.original_price ?? item.price; // cart returns original_price; guest cart may not
    const bestPct = Math.max(volumePct(qty), promoPct);
    const unit = +(original * (1 - bestPct / 100)).toFixed(2);
    subtotal += +(unit * qty).toFixed(2);
    originalSubtotal += +(original * qty).toFixed(2);
  });
  subtotal = +subtotal.toFixed(2);
  originalSubtotal = +originalSubtotal.toFixed(2);
  const savings = +(originalSubtotal - subtotal).toFixed(2);

  const shippingCost = calculateShipping(subtotal, shippingMethod);
  const orderTotal = +(subtotal + shippingCost).toFixed(2);
  const qualifiesForFreeShipping = subtotal >= FREE_SHIPPING_THRESHOLD;

  const handleInputChange = (e) => {
    setShippingAddress({ ...shippingAddress, [e.target.name]: e.target.value });
  };

  const handleGuestInfoChange = (e) => {
    setGuestInfo({ ...guestInfo, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;

      // Affiliate referral — only honoured within the advertised 180-day click window.
      const activeRef = getActiveAffiliateRef();
      const affiliateRef = activeRef ? activeRef.code : null;
      const affiliateClickedAt = activeRef ? activeRef.clickedAt : null;
      
      if (user) {
        // Authenticated checkout
        response = await axios.post(`${API}/payments/checkout`, {
          shipping_address: shippingAddress,
          origin_url: window.location.origin,
          shipping_method: shippingMethod,
          affiliate_ref: affiliateRef,
          affiliate_clicked_at: affiliateClickedAt,
          promo_code: appliedPromo ? appliedPromo.code : null
        }, { withCredentials: true });
      } else {
        // Guest checkout
        if (!guestInfo.email) {
          toast.error('Please enter your email address');
          setLoading(false);
          return;
        }
        
        response = await axios.post(`${API}/payments/guest-checkout`, {
          email: guestInfo.email,
          shipping_address: shippingAddress,
          origin_url: window.location.origin,
          shipping_method: shippingMethod,
          cart_items: guestCart.items.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity
          })),
          affiliate_ref: affiliateRef,
          affiliate_clicked_at: affiliateClickedAt,
          promo_code: appliedPromo ? appliedPromo.code : null
        });
        
        // Clear guest cart on successful order creation
        localStorage.removeItem('guest_cart');
      }
      
      // Clear affiliate ref after successful checkout (both storage + cookie)
      clearAffiliateRef();

      // Go to the Zelle payment instructions screen
      const d = response.data;
      navigate('/checkout/zelle', {
        state: {
          orderId: d.order_id,
          orderNumber: d.order_number,
          amount: d.amount_due,
          zelleRecipient: d.zelle_recipient,
          zelleName: d.zelle_name,
          customerEmail: d.customer_email,
        }
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = 'Failed to place your order';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map((e) => e?.msg || 'Invalid input').join(', ');
      } else if (detail?.msg) {
        msg = detail.msg;
      }
      toast.error(msg);
      setLoading(false);
    }
  };

  if (cartLoading && user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (activeCart.items.length === 0) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 text-[#1E2532] mx-auto mb-4" />
          <p className="text-[#A1A7B5] text-lg mb-4">Your cart is empty</p>
          <Button onClick={() => navigate('/products')} className="btn-primary">
            Browse Products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-[#A1A7B5] hover:text-white mb-8 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>

        <h1 
          className="text-3xl font-bold text-white mb-8"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Guest/Login Options */}
              {!user && (
                <div className="glass-card p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-[#00F0FF]/20 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-[#00F0FF]" />
                    </div>
                    <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Checkout Options
                    </h2>
                  </div>

                  <div className="space-y-4">
                    <p className="text-[#A1A7B5]">
                      Continue as a guest or sign in for a faster checkout experience.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link to="/login" state={{ from: { pathname: '/checkout' } }} className="flex-1">
                        <Button type="button" className="btn-secondary w-full" data-testid="checkout-login-btn">
                          Sign In
                        </Button>
                      </Link>
                      <Link to="/register" state={{ from: { pathname: '/checkout' } }} className="flex-1">
                        <Button type="button" className="btn-secondary w-full" data-testid="checkout-register-btn">
                          Create Account
                        </Button>
                      </Link>
                    </div>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-[#1E2532]"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-[#0C0E14] text-[#A1A7B5]">or continue as guest</span>
                      </div>
                    </div>

                    <div>
                      <label className="text-[#A1A7B5] text-sm mb-2 block">Email Address *</label>
                      <div className="relative">
                        <input
                          type="email"
                          name="email"
                          value={guestInfo.email}
                          onChange={handleGuestInfoChange}
                          placeholder="your@email.com"
                          className="input-field w-full pl-10"
                          required
                          data-testid="guest-email"
                        />
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
                      </div>
                      <p className="text-[#A1A7B5] text-xs mt-2">
                        We&apos;ll send your order confirmation to this email.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Shipping Address */}
              <div className="glass-card p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-[#00F0FF]/20 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#00F0FF]" />
                  </div>
                  <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Shipping Address
                  </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#A1A7B5] text-sm mb-2 block">First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={shippingAddress.firstName}
                      onChange={handleInputChange}
                      className="input-field w-full"
                      required
                      data-testid="checkout-firstname"
                    />
                  </div>
                  <div>
                    <label className="text-[#A1A7B5] text-sm mb-2 block">Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={shippingAddress.lastName}
                      onChange={handleInputChange}
                      className="input-field w-full"
                      required
                      data-testid="checkout-lastname"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[#A1A7B5] text-sm mb-2 block">Address</label>
                    <input
                      type="text"
                      name="address"
                      value={shippingAddress.address}
                      onChange={handleInputChange}
                      className="input-field w-full"
                      required
                      data-testid="checkout-address"
                    />
                  </div>
                  <div>
                    <label className="text-[#A1A7B5] text-sm mb-2 block">City</label>
                    <input
                      type="text"
                      name="city"
                      value={shippingAddress.city}
                      onChange={handleInputChange}
                      className="input-field w-full"
                      required
                      data-testid="checkout-city"
                    />
                  </div>
                  <div>
                    <label className="text-[#A1A7B5] text-sm mb-2 block">State</label>
                    <input
                      type="text"
                      name="state"
                      value={shippingAddress.state}
                      onChange={handleInputChange}
                      className="input-field w-full"
                      required
                      data-testid="checkout-state"
                    />
                  </div>
                  <div>
                    <label className="text-[#A1A7B5] text-sm mb-2 block">ZIP Code</label>
                    <input
                      type="text"
                      name="zipCode"
                      value={shippingAddress.zipCode}
                      onChange={handleInputChange}
                      className="input-field w-full"
                      required
                      data-testid="checkout-zipcode"
                    />
                  </div>
                  <div>
                    <label className="text-[#A1A7B5] text-sm mb-2 block">Country</label>
                    <input
                      type="text"
                      name="country"
                      value={shippingAddress.country}
                      onChange={handleInputChange}
                      className="input-field w-full"
                      required
                      data-testid="checkout-country"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Method */}
              <div className="glass-card p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-[#00F0FF]/20 rounded-full flex items-center justify-center">
                    <Truck className="w-5 h-5 text-[#00F0FF]" />
                  </div>
                  <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Shipping Method
                  </h2>
                </div>

                {/* Free Shipping Progress */}
                {!qualifiesForFreeShipping && (
                  <FreeShippingBar subtotal={subtotal} threshold={FREE_SHIPPING_THRESHOLD} />
                )}

                <div className="space-y-3">
                  {/* Standard Shipping */}
                  <label 
                    className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
                      shippingMethod === 'standard' 
                        ? 'border-2 border-[#00F0FF] bg-[#00F0FF]/5' 
                        : 'border border-[#1E2532] hover:border-[#00F0FF]/50'
                    }`}
                    data-testid="shipping-standard"
                  >
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="shipping"
                        value="standard"
                        checked={shippingMethod === 'standard'}
                        onChange={(e) => setShippingMethod(e.target.value)}
                        className="w-4 h-4 text-[#00F0FF] bg-[#1E2532] border-[#1E2532] focus:ring-[#00F0FF]"
                      />
                      <div className="flex items-center space-x-2">
                        <Truck className="w-5 h-5 text-[#A1A7B5]" />
                        <div>
                          <p className="text-white font-medium">Standard Shipping</p>
                          <p className="text-[#A1A7B5] text-sm">Up to 7 days for delivery</p>
                        </div>
                      </div>
                    </div>
                    <span className={`font-semibold ${qualifiesForFreeShipping ? 'text-green-400' : 'text-white'}`}>
                      {qualifiesForFreeShipping ? 'FREE' : `$${calculateShipping(subtotal, 'standard').toFixed(2)}`}
                    </span>
                  </label>

                  {/* Express Shipping */}
                  <label 
                    className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
                      shippingMethod === 'express' 
                        ? 'border-2 border-[#00F0FF] bg-[#00F0FF]/5' 
                        : 'border border-[#1E2532] hover:border-[#00F0FF]/50'
                    }`}
                    data-testid="shipping-express"
                  >
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="shipping"
                        value="express"
                        checked={shippingMethod === 'express'}
                        onChange={(e) => setShippingMethod(e.target.value)}
                        className="w-4 h-4 text-[#00F0FF] bg-[#1E2532] border-[#1E2532] focus:ring-[#00F0FF]"
                      />
                      <div className="flex items-center space-x-2">
                        <Zap className="w-5 h-5 text-yellow-400" />
                        <div>
                          <p className="text-white font-medium">Express Shipping</p>
                          <p className="text-[#A1A7B5] text-sm">2-3 business days</p>
                        </div>
                      </div>
                    </div>
                    <span className="text-white font-semibold">
                      ${calculateShipping(subtotal, 'express').toFixed(2)}
                    </span>
                  </label>
                </div>

                {qualifiesForFreeShipping && shippingMethod === 'standard' && (
                  <p className="text-green-400 text-sm mt-4 flex items-center space-x-2">
                    <span>🎉</span>
                    <span>You qualify for FREE standard shipping!</span>
                  </p>
                )}
              </div>

              {/* Payment */}
              <div className="glass-card p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-[#00F0FF]/20 rounded-full flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-[#00F0FF]" />
                  </div>
                  <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Payment — Zelle
                  </h2>
                </div>
                <p className="text-[#A1A7B5]">
                  We accept payment via <span className="text-white font-medium">Zelle</span>. After you place your order, you&apos;ll see simple instructions to send your payment and enter your Zelle confirmation number. Your order ships once we verify the payment.
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-4 text-lg"
                data-testid="checkout-submit-btn"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    <span>Placing order...</span>
                  </div>
                ) : (
                  `Place Order — Pay $${orderTotal.toFixed(2)} via Zelle`
                )}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="glass-card p-6 sticky top-24">
              <h2 className="text-xl font-semibold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Order Summary
              </h2>

              <div className="space-y-4 mb-6">
                {activeCart.items.map((item) => (
                  <div key={item.product_id} className="flex space-x-4" data-testid={`checkout-item-${item.product_id}`}>
                    <img
                      src={item.image_url || '/placeholder.png'}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-medium truncate">{item.name}</h4>
                      <p className="text-[#A1A7B5] text-sm">Qty: {item.quantity}</p>
                      <p className="text-[#00F0FF] font-semibold">${item.subtotal.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#1E2532] pt-4 space-y-2">
                {/* Promo code input */}
                {!appliedPromo ? (
                  <div className="pb-3 mb-2 border-b border-[#1E2532]" data-testid="promo-code-input-block">
                    <label className="flex items-center text-xs text-[#A1A7B5] uppercase tracking-wide mb-2">
                      <Tag className="w-3 h-3 mr-1.5" /> Promo or Referral Code
                    </label>
                    <div className="flex gap-2">
                      <Input
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyPromo(); } }}
                        placeholder="Enter code"
                        className="bg-[#111621] border-[#1E2532] text-white uppercase flex-1"
                        data-testid="promo-code-input"
                      />
                      <Button
                        type="button"
                        onClick={applyPromo}
                        disabled={promoLoading || !promoInput.trim()}
                        className="bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/40"
                        data-testid="promo-code-apply"
                      >
                        {promoLoading ? '…' : 'Apply'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 mb-2 bg-[#00F0FF]/5 border border-[#00F0FF]/30 rounded-lg" data-testid="promo-code-applied">
                    <div className="flex items-center gap-2 min-w-0">
                      <Check className="w-4 h-4 text-[#00F0FF] shrink-0" />
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">{appliedPromo.code}</p>
                        <p className="text-[#A1A7B5] text-xs truncate">{appliedPromo.description}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removePromo}
                      className="text-[#A1A7B5] hover:text-red-400 transition-colors ml-2"
                      data-testid="promo-code-remove"
                      aria-label="Remove promo code"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {savings > 0 && (
                  <div className="flex justify-between text-[#A1A7B5] text-sm" data-testid="checkout-original-subtotal">
                    <span>Subtotal (before discount)</span>
                    <span className="line-through">${originalSubtotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[#A1A7B5]">
                  <span>Subtotal</span>
                  <span data-testid="checkout-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                {savings > 0 && (
                  <div className="flex justify-between text-green-400 text-sm" data-testid="checkout-savings">
                    <span>You saved</span>
                    <span>-${savings.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[#A1A7B5]">
                  <span>Shipping ({shippingMethod === 'express' ? 'Express' : 'Standard'})</span>
                  <span className={shippingCost === 0 ? 'text-green-400' : ''}>
                    {shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between text-white font-bold text-lg pt-2 border-t border-[#1E2532]">
                  <span>Total</span>
                  <span data-testid="checkout-total">${orderTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
